<template>
  <div
    v-if="initialized"
    class="yhc-ef-kazai-kaguido-detail-input--select-destination mt-3"
  >
    <div class="card yhc-sub-card">
      <div
        v-for="question in visibleQuestions"
        :key="question.id"
      >
        <div
          class="card-header"
          v-html="question.questionText"
        ></div>
        <div
          class="card-body yhc-ef-kazai-kaguido-detail-input--select-destination-wrap--inner"
        >
          <div
            v-for="option in getOptionsForQuestion(question)"
            :key="option.id"
            class="yhc-ef-kazai-kaguido-detail-input--select-destination--option"
            @click="selectOption(question, option)"
          >
            <div
              class="yhc-option-item"
              :class="{ selected: isOptionSelected(question, option) }"
            >
              <span
                class="yhc-ef-kazai-kaguido-detail-input--select-destination--option--icon"
              >
                <ficon
                  :type="isOptionSelected(question, option) ? 'check-circle' : 'circle'"
                />
              </span>
              {{ option.optionText }}
              <span
                v-if="option.additionalPrice > 0"
                class="yhc-ef-kazai-kaguido-detail-input--select-destination--option--price"
              >
                ({{ formatPrice(option.additionalPrice) }})
              </span>
            </div>
          </div>
          <div
            v-if="question.description"
            class="small text-muted"
          >
            {{ question.description }}
          </div>
        </div>
      </div>
      <div class="card-body">
        <strong>金額: {{ formatPrice(totalPrice) }}</strong>
      </div>
      <div class="card-body">
        ※移動先の詳細は<span
          class="text-underline cursor-pointer"
          style="color: #0d6efd"
          @click.prevent="openDetailModal"
          >こちら</span
        >を参照ください。
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { YhcEstimateFormFrontService } from '../../YhcEstimateFormFrontService'
import {
  ModelKazaiKaguidoOptionDestinationQuestionAndAnswer,
  ModelYhcKazaiKaguidoDestinationQuestions,
} from '../../../../fireModels/yhcDataTypes'
import YhcEFKazaiKaguidoDetailInputDescriptionModal from './YhcEFKazaiKaguidoDetailInputDescriptionModal.vue'

type ModelYhcKazaiKaguidoDestinationQuestionsOfNonRelation = Omit<
  ModelYhcKazaiKaguidoDestinationQuestions,
  'parentQuestion' | 'dependentOptions'
> & {
  parentQuestion: number
  dependentOptions: {
    dependentOption: number
  }[]
}

export default {
  name: 'YhcEFKazaiKaguidoDetailInputSelectDestination',
  inject: ['YhcEstimateFormFrontServiceInstance'],
  props: {
    selectedDestinationQuestionAndAnswerByQuestionId: {
      type: Object as () => {
        [questionId: number]: ModelKazaiKaguidoOptionDestinationQuestionAndAnswer
      },
      default: null,
    },
  },
  computed: {
    $yhcefs(): YhcEstimateFormFrontService {
      return this.YhcEstimateFormFrontServiceInstance
    },
    questions(): ModelYhcKazaiKaguidoDestinationQuestionsOfNonRelation[] {
      return this.$yhcefs.kaguidoOptionQuestions
    },
    visibleQuestions(): ModelYhcKazaiKaguidoDestinationQuestionsOfNonRelation[] {
      return this.questions.filter((question) => this.isQuestionVisible(question))
    },
    totalPrice() {
      return Object.values(this.selectedOptions).reduce((total, optionId) => {
        const option = this.$yhcefs.kaguidoOptionSelections.find(
          (opt) => opt.id === optionId,
        )
        return total + (option?.additionalPrice || 0)
      }, 0)
    },
  },
  data() {
    return {
      initialized: false,
      selectedOptions: {} as { [questionId: number]: number },
    }
  },
  async mounted() {
    await this.initSelectedOptions()
    // 質問と回答選択情報の初期値をemitする
    this.$emit(
      'selectDestinations',
      (await this.generateSelectedDestinations()) as {
        [questionId: number]: ModelKazaiKaguidoOptionDestinationQuestionAndAnswer
      },
    )
    this.$nextTick(() => {
      this.initialized = true
    })
  },
  methods: {
    async initSelectedOptions() {
      // selectedDestinationQuestionAndAnswerByQuestionIdが存在すれば初期値を設定
      if (this.selectedDestinationQuestionAndAnswerByQuestionId) {
        for (const questionId in this.selectedDestinationQuestionAndAnswerByQuestionId) {
          const answer = this.selectedDestinationQuestionAndAnswerByQuestionId[questionId]
          this.selectedOptions = {
            ...this.selectedOptions,
            [Number(questionId)]: answer.optionId,
          }
        }
      }
    },
    getOptionsForQuestion(question) {
      return this.$yhcefs.kaguidoOptionSelections.filter(
        (option) => option.question.id === question.id,
      )
    },
    isOptionSelected(question, option) {
      return this.selectedOptions[question.id] === option.id
    },
    async selectOption(question, option) {
      this.selectedOptions = {
        ...this.selectedOptions,
        [question.id]: option.id,
      }
      // 子質問の選択をクリア
      this.clearChildSelections(question)
      // 質問と回答選択情報をemit
      this.$emit(
        'selectDestinations',
        (await this.generateSelectedDestinations()) as {
          [questionId: number]: ModelKazaiKaguidoOptionDestinationQuestionAndAnswer
        },
      )
    },
    formatPrice(price) {
      return `+¥${price.toLocaleString()}`
    },
    openDetailModal() {
      // 詳細説明モーダル
      $core.$modals.openModal({
        component: YhcEFKazaiKaguidoDetailInputDescriptionModal,
        modalProps: {
          size: 'xs',
        },
      })
    },
    isQuestionVisible(question: ModelYhcKazaiKaguidoDestinationQuestionsOfNonRelation) {
      if (!question.parentQuestion) {
        return true
      }
      const parentOption = this.selectedOptions[question.parentQuestion]
      if (!parentOption) {
        return false
      }
      // 親の選択肢に依存している場合の対応
      if (
        question.dependentOptions?.length > 0 &&
        question.dependentOptions[0].dependentOption
      ) {
        return this.isOptionOrAncestorSelected(
          question.parentQuestion,
          question.dependentOptions,
        )
      }
      return true
    },
    isOptionOrAncestorSelected(
      parentQuestionId: number,
      dependentOptions: { dependentOption: number }[],
    ): boolean {
      // すべての依存オプションが選択されているかチェック
      return dependentOptions.every(({ dependentOption }) => {
        let currentQuestionId = parentQuestionId
        while (currentQuestionId) {
          const selectedOption = this.selectedOptions[currentQuestionId]
          if (selectedOption === dependentOption) {
            return true // この依存オプションは選択されている
          }
          // 親の質問IDを取得
          currentQuestionId = this.questions.find(
            (q) => q.id === currentQuestionId,
          )?.parentQuestion
        }
        return false // この依存オプションは選択されていない
      })
    },
    clearChildSelections(question) {
      const childQuestions = this.questions.filter(
        (q) => q.parentQuestion === question.id,
      )
      childQuestions.forEach((childQuestion) => {
        this.selectedOptions = {
          ...this.selectedOptions,
        }
        delete this.selectedOptions[childQuestion.id]
        this.clearChildSelections(childQuestion)
      })
    },
    // emitする質問と回答情報を生成
    async generateSelectedDestinations(): Promise<{
      [questionId: number]: ModelKazaiKaguidoOptionDestinationQuestionAndAnswer
    }> {
      const selectedDestinations: {
        [questionId: number]: ModelKazaiKaguidoOptionDestinationQuestionAndAnswer
      } = {}

      // 表示されている全ての質問をループ
      for (const question of this.visibleQuestions) {
        const optionId = this.selectedOptions[question.id]
        const option = optionId
          ? this.$yhcefs.kaguidoOptionSelections.find((opt) => opt.id === optionId)
          : null

        selectedDestinations[question.id] = {
          questionTitle: question.questionTitle,
          optionId,
          optionText: option ? option.optionText : null,
          additionalPrice: option ? option.additionalPrice : 0,
        }
      }

      return selectedDestinations
    },
  },
}
</script>

<style lang="scss">
.yhc-ef-kazai-kaguido-detail-input--select-destination {
  .yhc-ef-kazai-kaguido-detail-input--select-destination-wrap--inner {
    display: flex;
    flex-wrap: wrap;
    gap: var(--yhc-base-pad-sm);
    .yhc-ef-kazai-kaguido-detail-input--select-destination--option {
      .yhc-ef-kazai-kaguido-detail-input--select-destination--option--icon {
        opacity: 0.3;
      }
      .yhc-ef-kazai-kaguido-detail-input--select-destination--option--price {
        font-size: 0.8em;
        font-weight: normal;
        margin-top: 0.3em;
      }
      .yhc-card-content--notice {
        font-size: 0.8em;
        margin-top: 0.2em;
        color: #90a3b6;
      }
    }
  }
}
</style>
